import _invariant                               from 'invariant';
import PropTypes                                from 'prop-types';
import { connect }                              from 'react-redux';
import { MapResourceCollectionApiUrlPropTypes } from './mapResourceCollectionApiUrl';
import { buildMethodsByActionGroup }            from '../../actions/utils/apiResources/buildSyncActions';
import {
  createSelectResourceErrorForApiUrl,
  createSelectResourceInProgressForApiUrl
}                                               from '../../selectors/resourceSelectors';
import UserFriendlyError                        from '../../errors/UserFriendlyError';
import {
  MAP_RESOURCE_PROP_API_FETCH,
  MAP_RESOURCE_PROP_API_FETCH_ERROR,
  MAP_RESOURCE_PROP_API_IS_FETCHING,
  MAP_RESOURCE_PROP_API_URL,
}                                               from './mapResourceProps.const';
import { METHOD_GET }                           from '../../actions/utils/apiResources/apiResourceActions.const';


const method = METHOD_GET;


/**
 * Creates a HOC to map the 'callApi()' props for components that need to load resources.
 *
 * This can be coupled with other api resource HOCs, as can be seen in mapResourceCollection().
 *
 * @param {string} resourceName Name of the resource.
 * @param {string} actionGroup  The action group to use (for determining the method).
 *
 * @return {function(*=): (ComponentClass<Omit<TInjectedProps, keyof {}> & {resourceName: *, apiUrl: *}> &
 *   {WrappedComponent: Component<TInjectedProps>})}
 */
const mapApiFetch = ({resourceName, actionGroup}) => (Component) => {

  _invariant(actionGroup, `Missing 'actionGroup'`);

  const makeMapState = () => {

    const selectResourceInProgressForApiUrl = createSelectResourceInProgressForApiUrl(resourceName);
    const selectResourceErrorForApiUrl      = createSelectResourceErrorForApiUrl(resourceName);

    return (state, {[MAP_RESOURCE_PROP_API_URL]: url}) => ({
      [MAP_RESOURCE_PROP_API_IS_FETCHING]: selectResourceInProgressForApiUrl(state, {method, url}),
      [MAP_RESOURCE_PROP_API_FETCH_ERROR]: selectResourceErrorForApiUrl(state, {method, url}),
    });

  };

  const mapDispatch = () => (dispatch, {resourceName, [MAP_RESOURCE_PROP_API_URL]: url}) => {

    const requestAction = buildMethodsByActionGroup[actionGroup](resourceName).request;

    const baseApiFetch = () => dispatch(requestAction({method, url}));

    // noinspection JSUnusedGlobalSymbols
    return {baseApiFetch};

  };

  const mergeProps = (stateProps, {baseApiFetch, ...dispatchProps}, ownProps) => {

    const {[MAP_RESOURCE_PROP_API_IS_FETCHING]: isLoading} = stateProps;

    if (!isLoading) {

      dispatchProps[MAP_RESOURCE_PROP_API_FETCH] = baseApiFetch;

    } else {

      dispatchProps[MAP_RESOURCE_PROP_API_FETCH] = () => {
      };

    }

    return Object.assign({}, ownProps, stateProps, dispatchProps);

  };

  const ConnectedComponent = connect(makeMapState, mapDispatch, mergeProps)(Component);

  ConnectedComponent.propTypes = {

    ...MapResourceCollectionApiUrlPropTypes,

  };

  ConnectedComponent.displayName = 'MapLoadResource';

  return ConnectedComponent;

};


export const MapApiFetchPropTypes = {

  // Flag indicating whether the resources are currently being loaded.
  [MAP_RESOURCE_PROP_API_IS_FETCHING]: PropTypes.bool,

  // Any error reported by the API during loading.
  [MAP_RESOURCE_PROP_API_FETCH_ERROR]: PropTypes.instanceOf(UserFriendlyError),

  // Callback to load resources.
  [MAP_RESOURCE_PROP_API_FETCH]: PropTypes.func.isRequired,

};


export default mapApiFetch;


// WEBPACK FOOTER //
// ./src/components/resources/mapApiFetch.js